import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import type {SketchParametricPointHandle} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SKETCH_PARAMETRIC_TOLERANCE} from '../core/SketchParametricToleranceConfig';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';

/**
 * 实体级对称关系公式编译器。
 *
 * 本类只消费 solver island 的纯数据句柄，并按“轴、源、镜像”有序三元组生成方程。
 * 运行时对象解析、镜像命令生命周期和写回均由各自上层负责，避免建立第二套对象式求解链。
 */
export class SketchEntitySymmetricConstraintFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 本编译器处理的关系类型。 */
    public readonly relationKinds = ['Symmetric'] as const;

    /**
     * 编译实体级对称关系。
     *
     * @param context 统一关系公式编译上下文。
     * @returns 已编译、已拒绝或不适用状态。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        if (context.constraintState.relationKind !== 'Symmetric') {
            return 'not-applicable';
        }

        const targetRefs = context.constraintState.targetRefs ?? [];

        if (targetRefs.length !== 3) {
            return this.reject(context, '实体级对称关系需要有序的“轴、源、镜像”三个直接目标');
        }

        const [axisRef, sourceRef, mirroredRef] = targetRefs;
        const axis = this.resolveAxis(context, axisRef!);

        if (!axis) {
            return 'rejected';
        }

        if (sourceRef!.targetKind === 'point' && mirroredRef!.targetKind === 'point') {
            const sourcePoint = context.pointHandles.get(sourceRef!.targetId);
            const mirroredPoint = context.pointHandles.get(mirroredRef!.targetId);

            if (!sourcePoint || !mirroredPoint) {
                return this.reject(context, '对称关系的源点或镜像点不在当前 solver island 中');
            }

            this.addSymmetricPointPair(context, axis, 'point', sourcePoint, mirroredPoint);
            return 'compiled';
        }

        if (sourceRef!.targetKind !== 'entity' || mirroredRef!.targetKind !== 'entity') {
            return this.reject(context, '对称关系的源与镜像目标必须同为点或同为实体');
        }

        const sourceFamily = this.resolveEntityFamily(context, sourceRef!.targetId);
        const mirroredFamily = this.resolveEntityFamily(context, mirroredRef!.targetId);

        if (!sourceFamily || sourceFamily !== mirroredFamily) {
            return this.reject(context, '对称关系两侧实体类型不一致或缺少完整参数结构');
        }

        switch (sourceFamily) {
            case 'line':
                return this.compileLine(context, axis, sourceRef!.targetId, mirroredRef!.targetId);
            case 'circle':
                return this.compileCircle(context, axis, sourceRef!.targetId, mirroredRef!.targetId);
            case 'arc':
                return this.compileArc(context, axis, sourceRef!.targetId, mirroredRef!.targetId);
            case 'ellipse':
                return this.compileEllipse(context, axis, sourceRef!.targetId, mirroredRef!.targetId);
            case 'parabola':
                return this.compileParabola(context, axis, sourceRef!.targetId, mirroredRef!.targetId);
            case 'spline':
                return this.compileSpline(context, axis, sourceRef!.targetId, mirroredRef!.targetId);
            case 'slot':
                return this.compileSlot(context, axis, sourceRef!.targetId, mirroredRef!.targetId);
            default:
                return this.reject(context, `参数内核不支持 ${sourceFamily} 实体级对称`);
        }
    }

    /**
     * 解析草图直线轴或外部固定轴。
     *
     * 外部轴通过关系的通用 `formulaPayload` 进入参数层；这份载荷每次快照重新生成，
     * 不进入关系领域对象，也不建立镜像专用运行时求解服务。
     *
     * @param context 公式编译上下文。
     * @param axisRef 有序三元组中的轴引用。
     * @returns 可参与方程的轴端点；载荷无效时返回 null 并登记拒绝原因。
     */
    private resolveAxis(
        context: SketchConstraintFormulaCompileContext,
        axisRef: NonNullable<SketchConstraintFormulaCompileContext['constraintState']['targetRefs']>[number],
    ): readonly [SketchParametricPointHandle, SketchParametricPointHandle] | null {
        if (axisRef.targetKind === 'entity') {
            const line = context.linePointHandles.get(axisRef.targetId);

            if (!line) {
                this.reject(context, '对称关系的第一目标不是可参数化的草图直线');
                return null;
            }
            return line;
        }

        if (axisRef.targetKind !== 'reference') {
            this.reject(context, '对称关系的第一目标必须是草图直线或稳定外部轴引用');
            return null;
        }

        const payload = context.constraintState.formulaPayload;

        if (!payload || typeof payload !== 'object' || Array.isArray(payload)
            || Reflect.get(payload, 'kind') !== 'mirror-axis-line'
            || Reflect.get(payload, 'referenceId') !== axisRef.targetId) {
            this.reject(context, `外部镜向轴 ${axisRef.targetId} 当前悬空或缺少本轮公式载荷`);
            return null;
        }

        const start = this.readVector2(Reflect.get(payload, 'start'));
        const end = this.readVector2(Reflect.get(payload, 'end'));

        if (!start || !end || Math.hypot(end[0] - start[0], end[1] - start[1])
            <= SKETCH_PARAMETRIC_TOLERANCE.degenerateLength) {
            this.reject(context, `外部镜向轴 ${axisRef.targetId} 的局部几何无效或退化`);
            return null;
        }

        const equationId = this.equationId(context);

        return [
            context.system.addPoint(`${equationId}:axis-start`, start[0], start[1], true),
            context.system.addPoint(`${equationId}:axis-end`, end[0], end[1], true),
        ];
    }

    /**
     * 依据 builder 已声明的结构映射判定实体族。
     *
     * @param context 公式编译上下文。
     * @param entityId 目标实体 ID。
     * @returns 参数化实体族；缺少完整结构时返回 null。
     */
    private resolveEntityFamily(
        context: SketchConstraintFormulaCompileContext,
        entityId: string,
    ): string | null {
        if (context.slotStructureParameterIds.has(entityId)) {
            return 'slot';
        }
        if (context.arcGeometryStates.has(entityId)) {
            return 'arc';
        }
        if (context.ellipseGeometryStates.has(entityId)) {
            return 'ellipse';
        }
        if (context.parabolaGeometryStates.has(entityId)) {
            return 'parabola';
        }
        if (context.splineMirrorStructures.has(entityId)) {
            return 'spline';
        }
        if (context.linePointHandles.has(entityId)) {
            return 'line';
        }
        if (context.centerPointHandles.has(entityId) && context.radiusParameterIds.has(entityId)) {
            return 'circle';
        }
        return null;
    }

    /**
     * 编译直线的有序起终点镜像。
     *
     * @param context 公式编译上下文。
     * @param axis 镜像轴端点。
     * @param sourceId 源直线 ID。
     * @param mirroredId 镜像直线 ID。
     * @returns 编译结果。
     */
    private compileLine(
        context: SketchConstraintFormulaCompileContext,
        axis: readonly [SketchParametricPointHandle, SketchParametricPointHandle],
        sourceId: string,
        mirroredId: string,
    ): SketchConstraintFormulaCompileResult {
        const source = context.linePointHandles.get(sourceId);
        const mirrored = context.linePointHandles.get(mirroredId);

        if (!source || !mirrored) {
            return this.reject(context, '直线对称关系缺少有序起终点');
        }

        this.addSymmetricPointPair(context, axis, 'start', source[0], mirrored[0]);
        this.addSymmetricPointPair(context, axis, 'end', source[1], mirrored[1]);
        return 'compiled';
    }

    /**
     * 编译圆的圆心镜像与等半径。
     *
     * @param context 公式编译上下文。
     * @param axis 镜像轴端点。
     * @param sourceId 源圆 ID。
     * @param mirroredId 镜像圆 ID。
     * @returns 编译结果。
     */
    private compileCircle(
        context: SketchConstraintFormulaCompileContext,
        axis: readonly [SketchParametricPointHandle, SketchParametricPointHandle],
        sourceId: string,
        mirroredId: string,
    ): SketchConstraintFormulaCompileResult {
        const sourceCenter = context.centerPointHandles.get(sourceId);
        const mirroredCenter = context.centerPointHandles.get(mirroredId);
        const sourceRadius = context.radiusParameterIds.get(sourceId);
        const mirroredRadius = context.radiusParameterIds.get(mirroredId);

        if (!sourceCenter || !mirroredCenter || !sourceRadius || !mirroredRadius) {
            return this.reject(context, '圆对称关系缺少圆心或半径参数');
        }

        this.addSymmetricPointPair(context, axis, 'center', sourceCenter, mirroredCenter);
        context.system.addEquation(SketchParametricEquationFactory.equalRadius(
            `${this.equationId(context)}:radius`,
            sourceRadius,
            mirroredRadius,
        ));
        return 'compiled';
    }

    /**
     * 编译圆弧的圆心、起终点镜像与等半径。
     *
     * @param context 公式编译上下文。
     * @param axis 镜像轴端点。
     * @param sourceId 源圆弧 ID。
     * @param mirroredId 镜像圆弧 ID。
     * @returns 编译结果。
     */
    private compileArc(
        context: SketchConstraintFormulaCompileContext,
        axis: readonly [SketchParametricPointHandle, SketchParametricPointHandle],
        sourceId: string,
        mirroredId: string,
    ): SketchConstraintFormulaCompileResult {
        const source = context.arcGeometryStates.get(sourceId);
        const mirrored = context.arcGeometryStates.get(mirroredId);

        if (!source || !mirrored) {
            return this.reject(context, '圆弧对称关系缺少圆心、起终点或半径参数');
        }

        this.addSymmetricPointPair(context, axis, 'center', source.center, mirrored.center);
        this.addSymmetricPointPair(context, axis, 'start', source.start, mirrored.start);
        this.addSymmetricPointPair(context, axis, 'end', source.end, mirrored.end);
        context.system.addEquation(SketchParametricEquationFactory.equalRadius(
            `${this.equationId(context)}:radius`,
            source.radiusParameterId,
            mirrored.radiusParameterId,
        ));
        return 'compiled';
    }

    /**
     * 编译完整或部分椭圆的定义点镜像。
     *
     * 反射会翻转局部框架手性，因此源短轴正端与镜像短轴负端配对；builder 已用中点、
     * 正交和点在曲线上方程维护各自椭圆结构，本编译器不重复声明内部几何。
     *
     * @param context 公式编译上下文。
     * @param axis 镜像轴端点。
     * @param sourceId 源椭圆 ID。
     * @param mirroredId 镜像椭圆 ID。
     * @returns 编译结果。
     */
    private compileEllipse(
        context: SketchConstraintFormulaCompileContext,
        axis: readonly [SketchParametricPointHandle, SketchParametricPointHandle],
        sourceId: string,
        mirroredId: string,
    ): SketchConstraintFormulaCompileResult {
        const source = context.ellipseGeometryStates.get(sourceId);
        const mirrored = context.ellipseGeometryStates.get(mirroredId);

        if (!source || !mirrored || !source.majorNegative || !source.minorNegative
            || !mirrored.majorNegative || !mirrored.minorNegative
            || Boolean(source.start) !== Boolean(source.end)
            || Boolean(mirrored.start) !== Boolean(mirrored.end)
            || Boolean(source.start) !== Boolean(mirrored.start)) {
            return this.reject(context, '椭圆对称关系缺少四向轴点，或完整/部分椭圆结构不一致');
        }

        this.addSymmetricPointPair(context, axis, 'center', source.center, mirrored.center);
        this.addSymmetricPointPair(
            context,
            axis,
            'major-positive',
            source.majorPositive,
            mirrored.majorPositive,
        );
        this.addSymmetricPointPair(
            context,
            axis,
            'minor-positive',
            source.minorPositive,
            mirrored.minorNegative,
        );

        if (source.start && source.end && mirrored.start && mirrored.end) {
            this.addSymmetricPointPair(context, axis, 'start', source.start, mirrored.start);
            this.addSymmetricPointPair(context, axis, 'end', source.end, mirrored.end);
        }
        return 'compiled';
    }

    /**
     * 编译抛物线焦点、顶点和可见段端点镜像。
     *
     * @param context 公式编译上下文。
     * @param axis 镜像轴端点。
     * @param sourceId 源抛物线 ID。
     * @param mirroredId 镜像抛物线 ID。
     * @returns 编译结果。
     */
    private compileParabola(
        context: SketchConstraintFormulaCompileContext,
        axis: readonly [SketchParametricPointHandle, SketchParametricPointHandle],
        sourceId: string,
        mirroredId: string,
    ): SketchConstraintFormulaCompileResult {
        const source = context.parabolaGeometryStates.get(sourceId);
        const mirrored = context.parabolaGeometryStates.get(mirroredId);

        if (!source || !mirrored || !source.start || !source.end || !mirrored.start || !mirrored.end) {
            return this.reject(context, '抛物线对称关系缺少焦点、顶点或可见段端点');
        }

        this.addSymmetricPointPair(context, axis, 'focal', source.focal, mirrored.focal);
        this.addSymmetricPointPair(context, axis, 'apex', source.apex, mirrored.apex);
        this.addSymmetricPointPair(context, axis, 'start', source.start, mirrored.start);
        this.addSymmetricPointPair(context, axis, 'end', source.end, mirrored.end);
        return 'compiled';
    }

    /**
     * 编译标准样条或样式样条的有序可编辑控制点。
     *
     * @param context 公式编译上下文。
     * @param axis 镜像轴端点。
     * @param sourceId 源样条 ID。
     * @param mirroredId 镜像样条 ID。
     * @returns 编译结果。
     */
    private compileSpline(
        context: SketchConstraintFormulaCompileContext,
        axis: readonly [SketchParametricPointHandle, SketchParametricPointHandle],
        sourceId: string,
        mirroredId: string,
    ): SketchConstraintFormulaCompileResult {
        const source = context.splineMirrorStructures.get(sourceId);
        const mirrored = context.splineMirrorStructures.get(mirroredId);

        if (!source || !mirrored || source.variant !== mirrored.variant
            || source.controlPoints.length !== mirrored.controlPoints.length) {
            return this.reject(context, '对称关系两侧样条变体或控制点数量不一致');
        }

        source.controlPoints.forEach((sourcePoint, index) => {
            this.addSymmetricPointPair(
                context,
                axis,
                `control:${index}`,
                sourcePoint,
                mirrored.controlPoints[index]!,
            );
        });
        return 'compiled';
    }

    /**
     * 编译直槽口或圆弧槽口的最小独立结构自由度。
     *
     * @param context 公式编译上下文。
     * @param axis 镜像轴端点。
     * @param sourceId 源槽口 ID。
     * @param mirroredId 镜像槽口 ID。
     * @returns 编译结果。
     */
    private compileSlot(
        context: SketchConstraintFormulaCompileContext,
        axis: readonly [SketchParametricPointHandle, SketchParametricPointHandle],
        sourceId: string,
        mirroredId: string,
    ): SketchConstraintFormulaCompileResult {
        const source = context.slotStructureParameterIds.get(sourceId);
        const mirrored = context.slotStructureParameterIds.get(mirroredId);

        if (!source || !mirrored) {
            return this.reject(context, '槽口对称关系缺少权威结构参数');
        }

        const sourceIsStraight = Boolean(source.axisHandles);
        const mirroredIsStraight = Boolean(mirrored.axisHandles);
        const sourceIsArc = Boolean(source.radiusParameterId && source.polarAnchorHandles?.length === 3);
        const mirroredIsArc = Boolean(mirrored.radiusParameterId && mirrored.polarAnchorHandles?.length === 3);

        if (sourceIsStraight !== mirroredIsStraight || sourceIsArc !== mirroredIsArc
            || (!sourceIsStraight && !sourceIsArc)) {
            return this.reject(context, '对称关系两侧槽口模式不一致或结构参数不完整');
        }

        context.system.addEquation(SketchParametricEquationFactory.scalarEqual(
            `${this.equationId(context)}:half-width`,
            source.halfWidthParameterId,
            mirrored.halfWidthParameterId,
        ));

        if (sourceIsStraight) {
            this.addSymmetricPointPair(
                context,
                axis,
                'axis-start',
                source.axisHandles![0],
                mirrored.axisHandles![0],
            );
            this.addSymmetricPointPair(
                context,
                axis,
                'axis-end',
                source.axisHandles![1],
                mirrored.axisHandles![1],
            );
            return 'compiled';
        }

        context.system.addEquation(SketchParametricEquationFactory.scalarEqual(
            `${this.equationId(context)}:radius`,
            source.radiusParameterId!,
            mirrored.radiusParameterId!,
        ));
        source.polarAnchorHandles!.forEach((sourcePoint, index) => {
            this.addSymmetricPointPair(
                context,
                axis,
                `arc-anchor:${index}`,
                sourcePoint,
                mirrored.polarAnchorHandles![index]!,
            );
        });
        return 'compiled';
    }

    /**
     * 加入一对完整二维镜像残差。
     *
     * @param context 公式编译上下文。
     * @param axis 镜像轴端点。
     * @param role 方程角色后缀。
     * @param sourcePoint 源点句柄。
     * @param mirroredPoint 镜像点句柄。
     */
    private addSymmetricPointPair(
        context: SketchConstraintFormulaCompileContext,
        axis: readonly [SketchParametricPointHandle, SketchParametricPointHandle],
        role: string,
        sourcePoint: SketchParametricPointHandle,
        mirroredPoint: SketchParametricPointHandle,
    ): void {
        context.system.addEquation(SketchParametricEquationFactory.symmetricPoints(
            `${this.equationId(context)}:${role}`,
            axis[0],
            axis[1],
            sourcePoint,
            mirroredPoint,
        ));
    }

    /**
     * 读取 plain-data 二维向量。
     *
     * @param value 待解析值。
     * @returns 有限二维元组；格式无效时返回 null。
     */
    private readVector2(value: unknown): readonly [number, number] | null {
        if (!Array.isArray(value) || value.length !== 2) {
            return null;
        }

        const [x, y] = value;

        return typeof x === 'number' && Number.isFinite(x)
            && typeof y === 'number' && Number.isFinite(y)
            ? [x, y]
            : null;
    }

    /**
     * 获取当前关系方程 ID 前缀。
     *
     * @param context 公式编译上下文。
     * @returns 稳定方程 ID 前缀。
     */
    private equationId(context: SketchConstraintFormulaCompileContext): string {
        return `constraint:${context.constraintState.constraintId}`;
    }

    /**
     * 登记显式不支持原因并返回拒绝状态。
     *
     * @param context 公式编译上下文。
     * @param reason 可定位原因。
     * @returns 拒绝状态。
     */
    private reject(
        context: SketchConstraintFormulaCompileContext,
        reason: string,
    ): SketchConstraintFormulaCompileResult {
        context.reportUnsupported(reason);
        return 'rejected';
    }
}
